import { T } from '../imports';
import axios from 'axios';

// TODO consolidate headers creation

const API_PORT = 3001;
const LOCAL_HOST = 'localhost';

/**
 * Get the appropriate host for API connections.
 * Priority:
 * 1. If REACT_APP_TARGET_ENV is set (e.g., npm start:beta), use EC2 IP from .env
 * 2. If accessed from network (not localhost), use current hostname
 * 3. Otherwise use localhost
 */
function getApiHost(): string {
    // Check if explicitly targeting an environment (e.g., npm start:beta)
    const targetEnv = process.env.REACT_APP_TARGET_ENV?.trim();
    if (targetEnv === 'beta' || targetEnv === 'prod') {
        const ec2IpKey = targetEnv === 'beta' 
            ? process.env.REACT_APP_EC2_PUBLIC_IP_BETA 
            : process.env.REACT_APP_EC2_PUBLIC_IP_PROD;
        
        if (ec2IpKey) {
            return ec2IpKey;
        }
        console.warn(`[HttpClient] REACT_APP_EC2_PUBLIC_IP_${targetEnv.toUpperCase()} not found in .env, falling back to localhost`);
    }

    // Check if we're being accessed from the network (not localhost)
    const currentHost = window.location.hostname;
    
    // If accessed via IP address or network hostname, use that for API calls
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
        return currentHost;
    }
    
    // Otherwise use localhost
    return LOCAL_HOST;
}

/**
 * Singleton that handles HTTP/REST API communication with the backend.
 * Manages authentication tokens, profile fetching, and broker key validation.
 */
class HttpClient {

    //#region Setup

    /** The server URL for HTTP/API requests */
    url: string = '';

    /** Auth0 token provider function. Set via setTokenProvider() */
    private tokenProvider: (() => Promise<string>) | null = null;

    /**
     * Sets the server URLs for the client based on the environment.
     */
    constructor() {
        const host = getApiHost(); // Use dynamic host detection
        const isLocalhost = host.includes('localhost');
        const isLAN = host.includes('192.168.');
        if (isLocalhost || isLAN) {
            this.url = `http://${host}:${API_PORT}/api`;
        } else {
            this.url = `https://${host}/api`;
        }

        console.log(`[HttpClient] API URL: ${this.url}`);
    }

    //#endregion
    

    //#region Environment settings

    get isDevMode(): boolean {
        return process.env.NODE_ENV === 'development';
    }

    /**
     * Manual override for testing production URLs in development mode.
     */
    setProdUrlsForDev() {
        const ec2Host = process.env.REACT_APP_EC2_PUBLIC_IP_PROD || process.env.REACT_APP_EC2_PUBLIC_IP_BETA || 'localhost';
        this.url = `https://${ec2Host}/api`;
    }

    //#endregion


    //#region Token Management

    /**
     * Initialize HttpClient with Auth0 token provider.
     * Call this once at app startup (e.g., in AppProvider).
     * @param getTokenFn Function that returns Auth0 access token
     */
    setTokenProvider(getTokenFn: () => Promise<string>): void {
        this.tokenProvider = getTokenFn;
    }

    /**
     * Get current Auth0 access token from the provider.
     * @private
     */
    private async getToken(): Promise<string> {
        if (!this.tokenProvider) {
            throw new Error('[HttpClient] Token provider not initialized. Call httpClient.setTokenProvider() in AppProvider.');
        }
        return this.tokenProvider();
    }

    //#endregion


    //#region Authentication & Authorization

    /**
     * Submit scanner alert payload to webhook endpoint.
    * Uses HttpClient base URL so requests hit the API server (e.g. :3001 in local dev).
     */
    async submitScannerAlert(payload: {
        ticker: string;
        side: 'CALL' | 'PUT';
        clear: string;
        grade: string;
    }): Promise<{ message: string; data: any }> {
        const endpoint = this.url + '/webhook/alert';

        try {
            const res = await axios.post(
                endpoint,
                {
                    ticker: payload.ticker.trim().toUpperCase(),
                    side: payload.side.trim().toUpperCase(),
                    clear: payload.clear.trim(),
                    grade: payload.grade.trim(),
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            if (!result.success) {
                throw new Error(result.message || 'Webhook failed');
            }

            return {
                message: result.message || 'Scanner alert accepted',
                data: result.data,
            };
        } catch (error: any) {
            const serverMessage = error?.response?.data?.message;
            const rawBody = typeof error?.response?.data === 'string' ? error.response.data : '';
            const msg = serverMessage || rawBody || error?.message || 'Webhook failed';
            throw new Error(msg);
        }
    }

    /**
     * Establishes a backend session after Auth0 authentication.
     * Sends the Auth0 JWT token to the backend to create a managed session.
     * @returns ApiResponse (success)
     */
    async auth0Callback(): Promise<any> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/auth/callback';
        console.log('[HttpClient] Sending Auth0 callback request to:', endpoint);
        
        try {
            const res = await axios.post(
                endpoint,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const result: T.ApiResponse = res.data;

            if (result.success) {
                return result.data;
            } else {
                const errMsg = result.message || 'Auth0 callback failed';
                console.error('[HttpClient] ' + errMsg);
                throw errMsg;
            }
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] Auth0 callback error:', errMsg);
            throw errMsg;
        }
    }

    async devLogin(): Promise<{ token: string; user?: { sub?: string; name?: string; email?: string; picture?: string } }> {
        const endpoint = this.url + '/auth/dev-login';
        console.log('[HttpClient] Sending dev login request to:', endpoint);

        try {
            const res = await axios.post(
                endpoint,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            if (!result.success) {
                throw new Error(result.message || 'Dev login failed');
            }

            const data = result.data || {};
            const token = data.token || data.accessToken;
            if (!token) {
                throw new Error('Dev login response missing token');
            }

            return {
                token,
                user: data.user,
            };
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Dev login failed';
            console.error('[HttpClient] Dev login error:', errMsg);
            throw new Error(errMsg);
        }
    }

    /**
     * Records ToS acceptance on the backend.
     * @param tosVersion The version of the ToS being accepted
     */
    async tosAccept(tosVersion: number): Promise<void> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/auth/tos-accept';
        
        try {
            const res = await axios.post(
                endpoint,
                { tosVersion },
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const result: T.ApiResponse = res.data;

            if (result.success) {
                return;
            } else {
                const errMsg = result.message || 'ToS acceptance failed';
                console.error('[HttpClient] ' + errMsg);
                throw errMsg;
            }
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] ToS acceptance error:', errMsg);
            throw errMsg;
        }
    }

    /**
     * Fetch both user data and user preferences from the server.
     * @returns Object containing userData and userPrefs
     */
    async getUser(): Promise<{ userData: T.UserData; userPrefs: T.UserPrefs }> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/user/info';

        console.log('[HttpClient] Fetching user data from:', endpoint);
        
        try {
            const res = await axios.get(
                endpoint,
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const result: T.ApiResponse = res.data;
            console.log('[HttpClient] User data response:', result);

            if (result.success && result.data) {
                return result.data as { userData: T.UserData; userPrefs: T.UserPrefs };
            } else {
                const errMsg = result.message || 'Failed to fetch user data';
                console.error('[HttpClient] ' + errMsg);
                throw errMsg;
            }
        } catch (error: any) {
            console.log(error);
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] User fetch error:', errMsg);
            throw errMsg;
        }
    }

    /**
     * Save user data to the backend.
     * Currently supports: lastVersionBadgeDismissed
     */
    async putUserData(data: {
        lastVersionBadgeDismissed?: string;
    }): Promise<void> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/user/data';
        
        try {
            const res = await axios.put(
                endpoint,
                data,
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const result: T.ApiResponse = res.data;

            if (result.success) {
                return;
            } else {
                const errMsg = result.message || 'Failed to save user data';
                console.error('[HttpClient] ' + errMsg);
                throw errMsg;
            }
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] User data save error:', errMsg);
            throw errMsg;
        }
    }

    /**
     * Save user preferences to the backend.
     * Supports any combination of: uiPrefs, tradePrefs, miscPrefs
     */
    async putUserPrefs(prefs: {
        uiPrefs?: T.UiPrefs;
        tradePrefs?: T.TradePrefs;
        miscPrefs?: T.MiscPrefs;
    }): Promise<void> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/user/prefs';
        
        try {
            const res = await axios.put(
                endpoint,
                prefs,
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const result: T.ApiResponse = res.data;

            if (result.success) {
                return;
            } else {
                const errMsg = result.message || 'Failed to save user preferences';
                console.error('[HttpClient] ' + errMsg);
                throw errMsg;
            }
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] User preferences save error:', errMsg);
            throw errMsg;
        }
    }

    /**
     * Get Snaptrade connection portal URL for linking brokerage accounts.
     * @returns The URL string to redirect the user to.
     */
    async getSnaptradeConnectionPortalUrl(): Promise<string> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/snaptrade/connectionUrl';

        try {
            const res = await axios.get(
                endpoint,
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;

            if (result.success && result.data) {
                return result.data as string;
            } else {
                const errMsg = result.message || 'Failed to get Snaptrade connection URL';
                console.error('[HttpClient] ' + errMsg);
                throw errMsg;
            }
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] ' + errMsg);
            throw errMsg;
        }
    }

    /**
        * Get connected brokerage accounts for the current user.
        * Endpoint also includes paper account information when available.
        * @returns ApiResponse with array of connected accounts
     */
    async getSnaptradeAccounts(): Promise<T.ApiResponse> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/snaptrade/accounts';

        try {
            const res = await axios.get(
                endpoint,
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            // console.log('[HttpClient] Snaptrade accounts response:', result);
            return result;
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] Snaptrade accounts error:', errMsg);
            return {
                success: false,
                message: errMsg
            };
        }
    }

    /**
     * Get the currently active SnapTrade account.
     * @returns ApiResponse with accountId or null
     */
    async getActiveSnaptradeAccount(): Promise<T.ApiResponse> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/snaptrade/activeAccount';

        try {
            const res = await axios.get(
                endpoint,
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            return result;
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] Active account error:', errMsg);
            return {
                success: false,
                message: errMsg
            };
        }
    }

    /**
     * Activate a specific SnapTrade account.
     * @param accountId The account ID to activate
     * @returns ApiResponse
     */
    async activateSnaptradeAccount(accountId: string): Promise<T.ApiResponse> {
        const auth0Token = await this.getToken();

        const endpoint = this.url + '/snaptrade/activateAccount';

        try {
            const res = await axios.post(
                endpoint,
                { accountId },
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            return result;
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] Activate account error:', errMsg);
            return {
                success: false,
                message: errMsg
            };
        }
    }

    /**
     * Create/activate the user's paper trading account.
     * @param startingCash Starting account cash (>= 0)
     */
    async activatePaperAccount(startingCash: number): Promise<T.ApiResponse> {
        const auth0Token = await this.getToken();
        const endpoint = this.url + '/paper/activateAccount';

        try {
            const res = await axios.post(
                endpoint,
                { startingCash },
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            return result;
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] Activate paper account error:', errMsg);
            return {
                success: false,
                message: errMsg
            };
        }
    }

    /**
     * Reset the user's paper trading account.
     * @param startingCash Starting account cash (>= 0)
     */
    async resetPaperAccount(startingCash: number): Promise<T.ApiResponse> {
        const auth0Token = await this.getToken();
        const endpoint = this.url + '/paper/resetAccount';

        try {
            const res = await axios.post(
                endpoint,
                { startingCash },
                {
                    headers: {
                        'Authorization': `Bearer ${auth0Token}`,
                        'Content-Type': 'application/json',
                    }
                }
            );

            const result: T.ApiResponse = res.data;
            return result;
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || 'Server not responding.';
            console.error('[HttpClient] Reset paper account error:', errMsg);
            return {
                success: false,
                message: errMsg
            };
        }
    }

    //#endregion
}

export const httpClient = new HttpClient();
